import { useState, useCallback, useEffect, useRef } from "react";
import { Typography } from "../components/atoms/Typography";
import { Button } from "../components/atoms/Button";
import { Input } from "../components/atoms/Input";
import { LegalEntitiesTable } from "../components/organisms/LegalEntitiesTable";
import { AddLegalEntityDrawer } from "../components/organisms/AddLegalEntityDrawer";
import { Building, Filter, Search } from "lucide-react";
import { twMerge } from "tailwind-merge";
import type {
  LegalEntity,
  LegalEntityStatus,
  LegalEntityFormData
} from "../types/legalEntity";
import {
  listLegalEntities,
  createLegalEntity,
  updateLegalEntityStatus,
  type LegalEntityListParams,
  type LegalEntitySortBy
} from "../services/legalEntityService";
import { apiRequest } from "../services/apiClient";

type FiltersState = {
  readonly status: LegalEntityStatus[];
  readonly modifiedBy: string[]; // user_id(s)
};

type UserOption = { readonly user_id: string; readonly user_name: string };

const debounceMs = 400;

export const LegalEntityPage = () => {
  const [entities, setEntities] = useState<LegalEntity[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [filterOpen, setFilterOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [totalRows, setTotalRows] = useState(0);
  const [filters, setFilters] = useState<FiltersState>({
    status: [],
    modifiedBy: []
  });
  const [sortBy, setSortBy] = useState<LegalEntitySortBy>("created_at");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");
  const [userOptions, setUserOptions] = useState<UserOption[]>([]);
  const filterRef = useRef<HTMLDivElement>(null);
  const searchTimerRef = useRef<number | null>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        filterRef.current &&
        !filterRef.current.contains(event.target as Node)
      ) {
        setFilterOpen(false);
      }
    };
    if (filterOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [filterOpen]);

  const loadEntities = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const params: LegalEntityListParams = {
        limit: perPage,
        offset: (page - 1) * perPage,
        q: searchQuery,
        status: filters.status,
        updatedBy: filters.modifiedBy,
        sort_by: sortBy,
        sort_dir: sortDir
      };
      const result = await listLegalEntities(params);
      setEntities(result.items);
      setTotalRows(result.total);
    } catch {
      setError("Unable to load legal entities. Please try again.");
    } finally {
      setIsLoading(false);
    }
  }, [filters.modifiedBy, filters.status, page, perPage, searchQuery, sortBy, sortDir]);

  useEffect(() => {
    void loadEntities();
  }, [loadEntities]);

  useEffect(() => {
    void (async () => {
      try {
        const response = await apiRequest("/users");
        if (!response.ok) {
          return;
        }
        const json = (await response.json()) as { data?: UserOption[] };
        setUserOptions(Array.isArray(json.data) ? json.data : []);
      } catch {
        setUserOptions([]);
      }
    })();
  }, []);

  const hasActiveFilters =
    filters.status.length > 0 || filters.modifiedBy.length > 0;

  const handleToggleStatus = useCallback(
    (id: string, newStatus: LegalEntityStatus) => {
      setEntities((prev) =>
        prev.map((e) => (e.id === id ? { ...e, status: newStatus } : e))
      );
      void (async () => {
        try {
          await updateLegalEntityStatus(id, newStatus);
        } catch {
          setEntities((prev) =>
            prev.map((e) =>
              e.id === id
                ? {
                  ...e,
                  status: newStatus === "Active" ? "Inactive" : "Active"
                }
                : e
            )
          );
          // eslint-disable-next-line no-alert
          alert("Failed to update status. Please try again.");
        }
      })();
    },
    []
  );

  const handleSaveLegalEntity = useCallback(
    async (formData: LegalEntityFormData) => {
      await createLegalEntity(formData);
      setPage(1);
      await loadEntities();
    },
    [loadEntities]
  );

  const handleSort = useCallback(
    (newSortBy: LegalEntitySortBy, newSortDir: "asc" | "desc") => {
      setSortBy(newSortBy);
      setSortDir(newSortDir);
      setPage(1);
    },
    []
  );

  const handleSearchChange = useCallback(
    (value: string) => {
      setSearchQuery(value);
      setPage(1);
      if (searchTimerRef.current) {
        window.clearTimeout(searchTimerRef.current);
      }
      searchTimerRef.current = window.setTimeout(() => {
        void loadEntities();
      }, debounceMs);
    },
    [loadEntities]
  );

  return (
    <section className="page-section">
      <div className="page-header">
        <div>
          <Typography as="h1" variant="h1">
            Legal Entity
          </Typography>
        </div>
      </div>

      <div ref={filterRef}>
        <div className="page-toolbar">
          <div className="search-wrapper">
            <Search className="search-icon" />
            <Input
              value={searchQuery}
              onChange={(e) => handleSearchChange(e.target.value)}
              placeholder="Search by Legal Entity Name or Address..."
              aria-label="Search by Legal Entity Name or Address"
              className="bg-white pl-10"
            />
          </div>
          <button
            type="button"
            onClick={() => setFilterOpen(!filterOpen)}
            className={twMerge(
              "filter-btn",
              filterOpen && "filter-btn--active"
            )}
            aria-expanded={filterOpen}
          >
            <Filter className="h-4 w-4" />
            Filter
          </button>
          <div className="ml-auto">
            <Button
              className="flex items-center gap-2"
              onClick={() => setIsDrawerOpen(true)}
            >
              <Building className="h-4 w-4" />
              Add Legal Entity
            </Button>
          </div>
        </div>

        {filterOpen && (
          <div
            className="filter-panel"
            role="region"
            aria-label="Filter options"
          >
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label
                  className="mb-2 block text-xs font-semibold uppercase tracking-wide text-gray-500"
                  id="filter-status-label"
                >
                  STATUS
                </label>
                <div className="space-y-2" role="group" aria-labelledby="filter-status-label">
                  {(["Active", "Inactive"] as const).map((option) => (
                    <label
                      key={option}
                      className="flex cursor-pointer items-center gap-2"
                    >
                      <input
                        type="checkbox"
                        checked={filters.status.includes(option)}
                        onChange={(e) => {
                          setFilters((prev) => ({
                            ...prev,
                            status: e.target.checked
                              ? [...prev.status, option]
                              : prev.status.filter((s) => s !== option)
                          }));
                          setPage(1);
                        }}
                        className="h-4 w-4 rounded border-gray-300 accent-primary-500 focus:ring-primary-500"
                      />
                      <span className="text-sm text-gray-800">
                        {option}
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <label
                  className="mb-2 block text-xs font-semibold uppercase tracking-wide text-gray-500"
                  id="filter-modifiedby-label"
                >
                  MODIFIED BY
                </label>
                <div
                  className="max-h-32 space-y-2 overflow-y-auto"
                  role="group"
                  aria-labelledby="filter-modifiedby-label"
                >
                  {userOptions.map((u) => (
                    <label
                      key={u.user_id}
                      className="flex cursor-pointer items-center gap-2"
                    >
                      <input
                        type="checkbox"
                        checked={filters.modifiedBy.includes(u.user_id)}
                        onChange={(e) => {
                          setFilters((prev) => ({
                            ...prev,
                            modifiedBy: e.target.checked
                              ? [...prev.modifiedBy, u.user_id]
                              : prev.modifiedBy.filter((m) => m !== u.user_id)
                          }));
                          setPage(1);
                        }}
                        className="h-4 w-4 rounded border-gray-300 accent-primary-500 focus:ring-primary-500"
                      />
                      <span className="text-sm text-gray-800">{u.user_name}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>

            {hasActiveFilters && (
              <div className="mt-4 border-t border-gray-200 pt-4">
                <button
                  type="button"
                  onClick={() =>
                    setFilters({ status: [], modifiedBy: [] })
                  }
                  className="rounded border border-primary-500 bg-transparent px-3 py-1.5 text-sm text-primary-500 transition-colors hover:bg-primary-500/10"
                >
                  Clear All Filters
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {error && (
        <div className="alert-error">{error}</div>
      )}

      <AddLegalEntityDrawer
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        onSave={handleSaveLegalEntity}
      />

      {isLoading ? (
        <Typography variant="muted">Loading legal entities…</Typography>
      ) : (
        <LegalEntitiesTable
          entities={entities}
          onToggleStatus={handleToggleStatus}
          totalRows={totalRows}
          isLoading={isLoading}
          page={page}
          perPage={perPage}
          onChangePage={(newPage) => setPage(newPage)}
          onChangeRowsPerPage={(newPerPage) => {
            setPerPage(newPerPage);
            setPage(1);
          }}
          sortBy={sortBy}
          sortDir={sortDir}
          onSort={handleSort}
        />
      )}
    </section>
  );
};
